import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";

import { Action, Selector, State, StateContext, Store } from "@ngxs/store";
import { tap } from "rxjs";

import {
  CheckoutAction,
  ClearAction,
  CreateOrderAction,
  GetOrderDetailsAction,
  GetOrdersAction,
  PlaceOrderAction,
  RePaymentAction,
  SaaScreateOrderAction,
  UpdateOrderAddressAction,
  VerifyPaymentAction,
  UpdateOrderMasterAction,
  ViewOrderAction,
} from "../action/order.action";
import { IApiResponse } from "../interface/api-response.interface";
import { IOrder, IOrderCheckout } from "../interface/order.interface";
import { OrderService } from "../services/order.service";

export class OrderStateModel {
  order = {
    data: [] as IOrder[],
    total: 0,
  };
  selectedOrder: IOrder | null;
  checkout: IOrderCheckout | null;
  createdOrder: IApiResponse | null;
  SaaSCreatedOrder: IApiResponse | null;
  UpdatedOrderAddress: IApiResponse | null;
  UpdatedOrderMaster: IApiResponse | null;
  orderDetails: IApiResponse | null;
}

@State<OrderStateModel>({
  name: "order",
  defaults: {
    order: {
      data: [],
      total: 0,
    },
    selectedOrder: null,
    checkout: null,
    createdOrder: null,
    SaaSCreatedOrder: null,
    UpdatedOrderAddress: null,
    UpdatedOrderMaster: null,
    orderDetails: null,
  },
})
@Injectable()
export class OrderState {
  private store = inject(Store);
  private router = inject(Router);
  private orderService = inject(OrderService);

  @Selector()
  static order(state: OrderStateModel) {
    return state.order;
  }

  @Selector()
  static selectedOrder(state: OrderStateModel) {
    return state.selectedOrder;
  }

  @Selector()
  static checkout(state: OrderStateModel) {
    return state.checkout;
  }

  @Selector()
  static createdOrder(state: OrderStateModel) {
    return state.createdOrder;
  }

  @Selector()
  static SaaSCreatedOrder(state: OrderStateModel) {
    return state.SaaSCreatedOrder;
  }

  @Selector()
  static UpdateOrderMaster(state: OrderStateModel) {
    return state.UpdatedOrderMaster;
  }
  @Selector()
  static UpdateOrderAddress(state: OrderStateModel) {
    return state.UpdatedOrderAddress;
  }
  @Selector()
  static orderDetails(state: OrderStateModel) {
    return state.orderDetails;
  }

  @Action(GetOrdersAction)
  getOrders(ctx: StateContext<OrderStateModel>, action: GetOrdersAction) {
    return this.orderService.getOrders(action?.payload).pipe(
      tap({
        next: (result) => {
          ctx.patchState({
            order: {
              data: result.data,
              total: result?.total
                ? result?.total
                : result.data
                  ? result.data.length
                  : 0,
            },
          });
        },
        error: (err) => {
          throw new Error(err?.error?.message);
        },
      })
    );
  }

  @Action(ViewOrderAction)
  viewOrder(ctx: StateContext<OrderStateModel>, { id }: ViewOrderAction) {
    this.orderService.skeletonLoader = true;
    return this.orderService.getOrders().pipe(
      tap({
        next: (result) => {
          const state = ctx.getState();
          const order = result.data.find((order) => order.order_number == id);

          ctx.patchState({
            ...state,
            selectedOrder: order,
          });
        },
        error: (err) => {
          throw new Error(err?.error?.message);
        },
        complete: () => {
          this.orderService.skeletonLoader = false;
        },
      })
    );
  }

  @Action(CheckoutAction)
  checkout(ctx: StateContext<OrderStateModel>, _action: CheckoutAction) {
    const state = ctx.getState();

    // It Just Static Values as per cart default value (When you are using api then you need calculate as per your requirement)
    const order = {
      total: {
        convert_point_amount: -10,
        convert_wallet_balance: -84.4,
        coupon_total_discount: 10,
        points: 300,
        points_amount: 10,
        shipping_total: 0,
        sub_total: 35.19,
        tax_total: 2.54,
        total: 37.73,
        wallet_balance: 84.4,
      },
    };

    ctx.patchState({
      ...state,
      checkout: order,
    });
  }

  @Action(PlaceOrderAction)
  placeOrder(ctx: StateContext<OrderStateModel>, action: PlaceOrderAction) {
    const state = ctx.getState();

    ctx.patchState({
      ...state,
      createdOrder: {
        success: true,
        data: action.payload,
      } as IApiResponse,
    });
  }

  @Action(CreateOrderAction)
  createOrder(ctx: StateContext<OrderStateModel>, action: CreateOrderAction) {
    return this.orderService.createOrder(action.payload).pipe(
      tap({
        next: (result) => {
          // Store the actual API response in state
          ctx.patchState({
            ...ctx.getState(),
            createdOrder: result,
          });
        },
        error: (err) => {
          throw new Error(err?.error?.message);
        },
      })
    );
  }

  @Action(SaaScreateOrderAction)
  SaaScreateOrder(
    ctx: StateContext<OrderStateModel>,
    action: SaaScreateOrderAction
  ) {
    return this.orderService.SaaScreateOrderNew(action.payload).pipe(
      tap({
        next: (result) => {
          // Store the actual API response in state
          ctx.patchState({
            ...ctx.getState(),
            SaaSCreatedOrder: result,
          });
        },
        error: (err) => {
          throw new Error(err?.error?.message);
        },
      })
    );
  }

  @Action(UpdateOrderMasterAction)
  updateOrderMaster(
    ctx: StateContext<OrderStateModel>,
    action: UpdateOrderMasterAction
  ) {
    return this.orderService.UpdateOrderMaster(action.id, action.payload).pipe(
      tap({
        next: (result) => {
          // Store the actual API response in state
          ctx.patchState({
            ...ctx.getState(),
            UpdatedOrderMaster: result,
          });
        },
        error: (err) => {
          // keep the HttpErrorResponse (status + body) so checkout can spot "Phone verification required."
          throw err;
        },
      })
    );
  }

  @Action(UpdateOrderAddressAction)
  updateOrderAddress(
    ctx: StateContext<OrderStateModel>,
    action: UpdateOrderAddressAction
  ) {
    return this.orderService.UpdateOrderAddress(action.id, action.payload).pipe(
      tap({
        next: (result) => {
          // Store the actual API response in state
          ctx.patchState({
            ...ctx.getState(),
            UpdatedOrderAddress: result,
          });
        },
        error: (err) => {
          throw new Error(err?.error?.message);
        },
      })
    );
  }

  @Action(GetOrderDetailsAction)
  getOrderDetails(
    ctx: StateContext<OrderStateModel>,
    action: GetOrderDetailsAction
  ) {
    return this.orderService.getOrderDetails(action.payload).pipe(
      tap({
        next: (result) => {
          // Store the order details in state
          ctx.patchState({
            ...ctx.getState(),
            orderDetails: result,
          });
          console.log("Order Details Retrieved:", result);
        },
        error: (err) => {
          console.error("Error fetching order details:", err);
          throw new Error(err?.error?.message);
        },
      })
    );
  }

  @Action(RePaymentAction)
  verifyPayment(_ctx: StateContext<OrderStateModel>, _action: RePaymentAction) {
    // Verify Payment Logic Here
  }

  @Action(VerifyPaymentAction)
  rePayment(_ctx: StateContext<OrderStateModel>, _action: VerifyPaymentAction) {
    // Re Payment Logic Here
  }

  @Action(ClearAction)
  clear(ctx: StateContext<OrderStateModel>) {
    const state = ctx.getState();
    ctx.patchState({
      ...state,
      checkout: null,
    });
  }
}
